import fire from '@store/firebase';
import {
  getCountFromServer,
  collection,
  query,
  where,
  getFirestore,
} from 'firebase/firestore';

import { ItimelineItem } from '@typings/pages/timeline.interface';
import { deleteImgStorage, saveAndGetImgURL } from '@store/firebase/storage';
import { updateHashtagsAndGetIdList } from '@store/firebase/hashtag/update-hashtag';
import { mentionUserNotificaton } from '../notification/update-notification';
import { processContentWithBots } from '@utils/customBot.util';
import { emitTimelineForceRefresh } from '@utils/realtimeFallbackEvent';

const firestore = fire.firestore();

export async function addAndGetDocId({ ref, data }) {
  return ref
    .add({
      ...data,
      createdAt: new Date(),
      editedAt: new Date(),
      hashtagIdList: [],
    })
    .then((res) => res.id)
    .catch((err) => console.log(err.message));
}

type Props = {
  data: ItimelineItem;
  collectionPath: string;
  docId?: string;
  hashtag: any;
  prevHashtag: any;
  fileList: {
    selectedFileList: File[] | null;
    prevImgURLList: string[];
    removeImgURLList: string[] | null;
  };
};

export async function updateTimeline({
  data,
  collectionPath,
  docId,
  hashtag,
  prevHashtag,
  fileList,
}: Props) {
  const ref = firestore.collection(collectionPath);
  const { selectedFileList, prevImgURLList, removeImgURLList } = fileList;

  let imgURLList: string[] = prevImgURLList || [];
  // 여러 이미지를 뽑아와야함.
  if (selectedFileList) {
    const newImgURLList = await Promise.all(
      selectedFileList.map(async (selectedFile: File, i) => {
        // 기존이미지는 삭제하고, 신규이미지는 추가
        if (selectedFile) {
          try {
            const resURL = await saveAndGetImgURL(collectionPath, selectedFile);
            return resURL;
          } catch (err) {
            alert(err.message);
          }
        }
      }),
    );

    const validNewImgURLList = newImgURLList.filter(
      (url): url is string => Boolean(url),
    );
    imgURLList = [...prevImgURLList, ...validNewImgURLList];
  }

  // 삭제된 이미지를 저장소에서도 삭제

  removeImgURLList?.map((url) => {
    if (url) deleteImgStorage(url);
  });

  let _docId: string = docId || '';
  // numberId는 신규 작성 시에만 계산 (수정 시에는 기존 값을 그대로 유지)
  let numberId: number | undefined;

  if (!docId) {
    // getCountFromServer (v9 modular API)를 사용해 서버에서 직접 실제 게시글 수를 집계한다.
    // createdAt 필드 기반 필터를 적용해 IdList 등 시스템 문서를 자동으로 제외한다.
    // compat API의 ref.get({ source: 'server' })는 내부 캐시를 무시하지 못하는 경우가 있어
    // 이 방식으로 교체한다.
    const db = getFirestore();
    const postsCountQuery = query(
      collection(db, collectionPath),
      where('createdAt', '>', new Date(0)),
    );
    const countSnap = await getCountFromServer(postsCountQuery);
    numberId = countSnap.data().count + 1;

    //추가
    _docId = await addAndGetDocId({ ref, data: { ...data, imgURLList } });
  }

  // hashtag 추가 및 재설정
  const hashtagIdList = await updateHashtagsAndGetIdList({
    hashtagList: hashtag,
    collectionPath: collectionPath,
    prevHashIdList: prevHashtag,
    docId: _docId,
  });

  // 🎯 커스텀 봇 실행 (신규 작성만, 수정 시에는 실행 안 함)
  const isUpdate = !!docId; // docId가 있으면 수정 모드
  const { contents: processedContents, botResults } = await processContentWithBots(
    data.contents || '',
    isUpdate
  );

  if (botResults) {
    console.log('🤖 타임라인 봇 실행 결과:', botResults);
  }

  const newData: ItimelineItem = {
    ...data,
    contents: processedContents, // 🎯 원본 contents 그대로
    imgURLList,
    imgURL: '', //imgURL 비우기 (list[]로 대체)
    hashtagIdList,
    editedAt: new Date(),
    // 신규 작성 시에만 numberId 설정 (수정 시에는 필드를 포함하지 않아 기존 값 보존)
    ...(numberId !== undefined && { numberId }),
    // 🎯 봇 결과 저장 (신규 작성 시에만)
    ...(botResults ? { botResults } : {}),
  };

  // 알림 추가 -------------------------------
  const notificationData = {
    contents: data.contents,
    sender: data.authorUid!,
    createdAt: new Date(),
    collectionPath,
    is_seen: false,
    postId: _docId,
  };
  mentionUserNotificaton({ data: notificationData });
  // 알림 추가 end -------------------------------

  return ref
    .doc(_docId)
    .set(newData, { merge: true })
    .then(() => {
      emitTimelineForceRefresh({
        collectionPath,
        action: docId ? 'update' : 'create',
        docId: _docId,
        patch: newData as any,
      });
    })
    .catch((err) => console.log(err.message));
}
